import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { authStorage } from '../../lib/auth-storage'
import { env } from '../../lib/env'

type RealtimeEcho = Echo<'pusher'>

declare global {
  interface Window {
    Pusher: typeof Pusher
    Echo?: RealtimeEcho
  }
}

let echoInstance: RealtimeEcho | null = null

function getBroadcastAuthUrl(): string {
  return `${env.REACT_APP_API_URL.replace(/\/$/, '')}/broadcasting/auth`
}

function isRealtimeConfigured(): boolean {
  return Boolean(env.REACT_APP_PUSHER_APP_KEY?.trim())
}

/** Laravel Echo + hosted Pusher Channels (pusher.com). */
export const realtimeService = {
  isConfigured(): boolean {
    return isRealtimeConfigured()
  },

  connect(): RealtimeEcho | null {
    if (!isRealtimeConfigured()) {
      return null
    }

    if (echoInstance) {
      return echoInstance
    }

    window.Pusher = Pusher

    const options: ConstructorParameters<typeof Echo<'pusher'>>[0] = {
      broadcaster: 'pusher',
      key: env.REACT_APP_PUSHER_APP_KEY as string,
      cluster: env.REACT_APP_PUSHER_APP_CLUSTER ?? 'mt1',
      forceTLS: true,
      enabledTransports: ['ws', 'wss'],
      authEndpoint: getBroadcastAuthUrl(),
      // Always read the latest Sanctum token — static auth.headers can be empty
      // if Echo connected before login finished storing the token.
      authorizer: (channel) => ({
        authorize: (socketId, callback) => {
          const token = authStorage.getToken()
          fetch(getBroadcastAuthUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              socket_id: socketId,
              channel_name: channel.name,
            }),
          })
            .then(async (response) => {
              if (!response.ok) {
                throw new Error(`Broadcast auth failed (${response.status})`)
              }

              return response.json()
            })
            .then((data) => callback(null, data))
            .catch((error: Error) => callback(error, null))
        },
      }),
    }

    echoInstance = new Echo(options)
    window.Echo = echoInstance

    return echoInstance
  },

  disconnect(): void {
    if (echoInstance) {
      echoInstance.disconnect()
      echoInstance = null
      delete window.Echo
    }
  },

  getEcho(): RealtimeEcho | null {
    return echoInstance
  },
}
